import BossModel from "@/models/BossModel";
import Product from '@/models/Product/Product';
import Code from "@/models/Agent/Code/Code";
import PackageType from "@/models/Package/PackageType/PackageType";
import Country from "@/models/Country/Country";
import WebsitePackage from "@/models/WebsitePackage/WebsitePackage";
import Region from "@/models/Region/Region";
import PackageInternalTag from "@/models/Package/PackageInternalTag/PackageInternalTag";
import Line from "@/models/Package/Line/Line";
import RelatedProduct from "@/models/RelatedProduct/RelatedProduct";
import WebsiteProductTag from "@/models/WebsiteProduct/Tag/Tag";
import Daytoday from "@/models/Package/Daytoday/Daytoday";
import UniqueSellingPoint from "@/models/Package/UniqueSellingPoint/UniqueSellingPoint";
import WebsiteRoute from "@/models/WebsiteRoute/WebsiteRoute";
import WebsiteIncludeTransfer from "@/models/WebsiteIncludeTransfer/WebsiteIncludeTransfer";
import WebsiteCustomerReview from "@/models/WebsiteCustomerReview/WebsiteCustomerReview";
import orderBy from 'lodash/orderBy';
import mapKeys from "lodash/mapKeys";
import groupBy from "lodash/groupBy";
import PackageInternalTagPackage from "@/models/Package/PackageInternalTagPackage";
import PackageRegion from "@/models/Package/PackageRegion";
import WebsiteHighlight from "@/models/WebsiteHighlight/WebsiteHighlight.js";

export default class Package extends BossModel {

    /**
     *
     * @type {string}
     */
    static apiUrl = 'package';

    /**
     *
     * @type {string}
     */
    static entity = 'packages';

    static fields() {
		return {
			id: this.number(null).nullable(),
            full_code: this.attr(null),
			agent_code_id: this.number(null).nullable(),
			package_type_id: this.number(null).nullable(),
			code: this.attr(null),
			minimum_nights: this.number(null).nullable(),
			maximum_nights: this.number(null).nullable(),
			title: this.attr(null),
			subtitle: this.attr(null),
			description: this.attr(null),
			url: this.attr(null),
			created_at: this.attr(null),
			updated_at: this.attr(null),
			deleted_at: this.attr(null),
			is_active: this.boolean(null),
			is_intern: this.boolean(null),
			is_published: this.boolean(null),
			country_id: this.number(null).nullable(),
			renewable_package_product_id: this.number(null).nullable(),
			stars: this.number(null).nullable(),
            website_highlight_id: this.number(null).nullable(),
            queued_for_today_at: this.attr(null),
            calculated_for_today_at: this.attr(null),
            agentCode: this.belongsTo(Code, 'agent_code_id'),
            type: this.belongsTo(PackageType, 'package_type_id'),
            country: this.belongsTo(Country, 'country_id'),
            // cachedPackagePrices: CachedPackagePrice,
            // cheapestCachedPackagePrices: CheapestCachedPackagePrice,
            // cheapestMonthlyCachedPackagePrices: CheapestMonthlyCachedPackagePrice,
            packageDaytodays: this.hasMany(Daytoday, 'package_id'),
            lines: this.hasMany(Line, 'package_id'),
            activeLines: this.hasMany(Line, 'package_id'),
            packageUsps: this.hasMany(UniqueSellingPoint, 'package_id'),
            websiteProductTags: this.hasMany(WebsiteProductTag, 'package_id'),
            relatedProducts: this.hasMany(RelatedProduct, 'package_id'),
            relatedProductsHotels: this.hasMany(RelatedProduct, 'package_id'),
            relatedProductsRelated: this.hasMany(RelatedProduct, 'package_id'),
            relatedProductsUpgrades: this.hasMany(RelatedProduct, 'package_id'),
            relatedProductsDowngrades: this.hasMany(RelatedProduct, 'package_id'),
            threeRelatedProducts: this.hasMany(RelatedProduct, 'package_id'),
            regions: this.belongsToMany(Region, PackageRegion, 'package_id', 'region_id'),
            packageInternalTags: this.belongsToMany(
                PackageInternalTag,
                PackageInternalTagPackage,
                'package_id',
                'package_internal_tag_id'
            ),
            // tags: Tag,
            // packages: Package,
            // queuedPackageCalculation: QueuedPackageCalculation,
            websiteCustomerReviews: this.hasMany(WebsiteCustomerReview, 'package_id'),
            websiteIncludeTransfers: this.hasMany(WebsiteIncludeTransfer, 'package_id'),
            // websiteOfferPackageRequests: WebsiteOfferPackageRequest,
            websitePackage: this.hasOne(WebsitePackage, 'package_id'),
            // productBlocks: ProductBlock,
            products: this.belongsToMany(Product, Line, 'package_id', 'product_id'),
            // activeProducts: Product,
            websiteRoute: this.hasOne(WebsiteRoute, 'routeable_id'),
            highlight: this.belongsTo(WebsiteHighlight, 'website_highlight_id'),
		}
	}

    /**
     *
     * @param id
     * @returns {*}
     */
    static loadWithRelations(id) {
        return this.api()
            .with([
                'tags',
                'regions',
                'country',
                'type',
                'agentCode.agent',
                'lines.product.activeVersion',
                'lines.product.activeVersion.flight.availabilities',
                'packageDaytodays.images.publicImage',
                'relatedProducts.toPackage',
                'relatedProducts.toProduct.activeVersion',
                'packageUsps',
                'websiteProductTags.tag.type',
                'websitePackage',
                'websitePackage.headerImage',
                'websitePackage.featuredImage',
                'websitePackage.websitePackageImages.publicImage',
                'websitePackage.regions',
                'websitePackage.countries',
                'websitePackage.websiteCategories',
                'websitePackage.packageTexts',
                'websiteRoute',
                'websiteIncludeTransfers',
            ])
            .find(id);
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static loadActiveLines(id) {
        return this.api()
            .with([
                'activeLines.product.activeActiveVersion',
                'activeLines.product.websiteProduct.productTexts',
            ])
            .find(id);
    }

    /**
     *
     * @param params
     * @returns {*}
     */
    static search(params = {}) {
        return this.api()
            .limit(20)
            .filtered(params.filters)
            .where('is_active', 1)
            .get();
    }

    /**
     *
     * @param filters
     * @param sortBy
     * @param paginator
     * @returns {*}
     */
    static searchByConditions(filters, sortBy, paginator){
        return this.api()
            .with((['country', 'type', 'websitePackage', 'lowestSalePrice']))
            .ordered(sortBy)
            .filtered(filters)
            .paginator(paginator)
            .get();

    }

    /**
     *
     * @param filters
     * @param limit
     * @returns {*}
     */
    static searchLimitedByConditions(filters, limit = 25){
        return this.api()
            .limit(limit)
            .with((['country', 'type', 'websitePackage', 'lowestSalePrice']))
            .filtered(filters)
            .get(false)
            .then(packages => {
                let models = [];
                packages.forEach(packageInstance => {
                    models.push(new this(packageInstance));
                });
                return models;
            });
    }

    /**
     *
     * @param filters
     * @param sortBy
     * @returns {*}
     */
    static fetchPackagesBySortedFilters(filters, sortBy) {
        return this.api()
            .where('is_active', true)
            .limit(30)
            .with('country')
            .with('lowestSalePrice')
            .ordered(sortBy)
            .filtered(filters)
            .get().then(packages => {
                let instances = [];
                if (Array.isArray(packages)) {
                    packages.forEach(p => {
                        instances.push(new this(p));
                    });
                }
                return instances;
            });
    }

    /**
     *
     * @returns {*}
     */
    fetchPackageDetailsWithProducts() {
        return this.constructor.api()
            .with('tags')
            .with('regions')
            .with('activeLines')
            .with('activeLines.product')
            .with('activeLines.product.activeActiveVersion.country')
            .with('activeLines.product.activeActiveVersion.flight')
            .with('activeLines.product.activeActiveVersion.flight.departureAirport')
            .with('activeLines.product.activeActiveVersion.flight.arrivalAirport')
            .find(this.id, false);
    }

    /**
     *
     * @param bookDate
     * @param departureDate
     * @param adults
     * @param children
     * @returns {*}
     */
    fetchPackageDetailPrices(bookDate, departureDate, adults, children) {
        return this.constructor.api()
            .action(this.id + '/products/options')
            .where('book_date', bookDate)
            .where('adults', adults)
            .where('children', children)
            .where('start_date', departureDate)
            .get();
    }

    /**
     *
     * @returns {*}
     */
    fetchPackageImages() {
        return this.constructor.api()
            .with('tags')
            .with('regions')
            .with([
                'websitePackage.headerImage',
                'websitePackage.featuredImage',
                'websitePackage.websitePackageImages.publicImage',
            ])
            .find(this.id, false);
    }

    /**
     *
     * @param tags
     * @returns {Promise<AxiosResponse<T>>}
     */
    syncInternalTags(tags) {
        return this.constructor.api().action(this.id + '/sync-internal-tags').update({'tags': tags}, false);
    }

    /**
     *
     * @param usps
     * @returns {Promise<AxiosResponse<T>>}
     */
    syncUsps(usps) {
        return this.constructor.api().action(this.id + '/sync-usps').update({'usps': usps}, false)
    }

    /**
     *
     * @param id
     * @returns {*}
     */
    static fetchPackageDaytodayImages(id){
        return this.api()
            .with([
                'packageDaytodays.images.publicImage',
            ])
            .find(id, false);
    }

    /**
     *
     * @returns {string|null}
     */
    FullUrl() {
        if (!this.Type() || !this.Country() || !this.url || this.url.length === 0) {
            return null;
        }

        return this.Country().url + '/' + this.Type().url + '/' + this.url;
    }

    /**
     *
     * @param isPublished
     * @param destination
     * @returns {Promise<AxiosResponse<T>>}
     */
    Publish(isPublished, destination = null) {
        let data = {
            'is_published': isPublished,
            'destination_website_route_id': null,
            'http_code': null,
        };

        if (destination !== null) {
            data.destination_website_route_id = destination.destination_website_route_id;
            data.http_code = destination.http_code;
        }

        return this.constructor.api()
            .action(this.id + '/publish')
            .update(data);
    }

    /**
     *
     * @returns {*}
     */
    WebsiteProductTags(){
        return this.getRelated('websiteProductTags');
    }

    /**
     *
     * @returns {*}
     */
    InternalTags(){
        return this.getRelated('packageInternalTags');
    }

    /**
     *
     * @returns {*}
     */
    InternalTagsByType(){
        const tags = this.InternalTags();
        return mapKeys(groupBy(tags, 'package_internal_tag_type_id'), (value, key) =>  key );
    }

    /**
     *
     * @returns {boolean}
     */
    IsProduct(){
        return false;
    }

    /**
     *
     * @returns {*}
     */
    RelatedProducts(){
        return this.getRelated('relatedProducts', function(item) {
            return item.relation_type_key === 'related';
        });
    }

    /**
     *
     * @returns {*}
     */
    Upgrades(){
        return this.getRelated('relatedProducts', function(item) {
            return item.relation_type_key === 'upgrade';
        });
    }

    /**
     *
     * @returns {*}
     */
    Downgrades(){
        return this.getRelated('relatedProducts', function(item) {
            return item.relation_type_key === 'downgrade';
        });
    }

    /**
     *
     * @returns {*}
     */
    RelatedHotels(){
        return this.getRelated('relatedProducts', function(item) {
            return item.relation_type_key === 'relatedHotel';
        });
    }

    /**
     *
     * @returns {boolean|*}
     */
    Country(){
        return this.getRelated('country');
    }

    /**
     *
     * @returns {*}
     */
    Lines(){
        return this.getRelated('lines');
    }

    /**
     *
     * @returns {*}
     */
    OptionalTourLines() {
        return this.$query().with('lines.product.activeVersion.type').find(this.id).lines.filter(packageLine =>
            packageLine.is_optional === true &&
            packageLine.product.activeVersion.is_published &&
            packageLine.product.activeVersion.type.code === 'T'
        );
    }

    /**
     *
     * @returns {boolean|*}
     */
    AgentCode(){
        return this.getRelated('agentCode');
    }

    /**
     *
     * @returns {*}
     */
    PackageUsps(){
        return this.getRelated('packageUsps');
    }

    /**
     *
     * @param ids
     * @returns {*}
     */
    static byIds(ids) {
        return this.all().filter(item => ids.includes(item.id));
    }

    /**
     *
     * @returns {string}
     */
    FullCode() {
        return this.Type().code + '-' + this.code;
    }

    /**
     *
     * @returns {*}
     */
    WebsitePackage(){
        return this.getRelated('websitePackage');
    }

    /**
     *
     * @returns {Array}
     */
    PackageDaytodays(){
        return orderBy(this.getRelated('packageDaytodays'), 'sort_order');
    }

    /**
     *
     * @returns {*}
     */
    WebsiteIncludeTransfers(){
        return this.getRelated('websiteIncludeTransfers');
    }

    /**
     *
     * @returns {*}
     */
    WebsiteRoute() {
        return WebsiteRoute.findByRouteable(this.id, 'package');
    }

    /**
     *
     * @returns {boolean|*}
     */
    Type(){
        return this.getRelated('type');
    }
}
